import React, {useEffect} from 'react'
import {Box, Chip, Collapse, FormControlLabel, IconButton, Stack, Switch, TextField, Typography} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import {useForm} from 'react-hook-form'
import {columnSpacing, defaultTextFieldWidth, FormIds} from "../../constants";
import {useTranslation} from "react-i18next";
import {useUserRole} from "../../utils/UserRoleContext";

export interface CustomerFormValues {
  name: string
  doctorName: string
  customerNumber: string
  streetAndDwellingNumber: string
  zip: string
  location: string
  biotronikClientName: string
  biotronikCustomerNumber: string
  eTin: string
  bostonSAPID: string
  abbottId: string
  microportId: string
  nonGbaGroupName: string
  gbaGroupName: string
  billingInHfGroups: boolean
  hfBillingGroups: string[]
}


interface UpdateCustomerFormProps {
  onSubmit: (data: CustomerFormValues) => void
  /** This function updates the dirty state in the parents component */
  updateDirty: (dirty: boolean) => void
  defaultValues: CustomerFormValues
}

export default function UpdateCustomerForm({onSubmit, defaultValues, updateDirty}: Readonly<UpdateCustomerFormProps>) {
  const { isAdmin } = useUserRole();

  const form = useForm<CustomerFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    defaultValues
  })

  const {t} = useTranslation()

  const {register, handleSubmit, formState, getValues, setValue, watch} = form

  const {errors} = formState


  useEffect(() => {
    updateDirty(form.formState.isDirty)
  }, [form.formState.isDirty])

  return (
    <form id={FormIds.updateCustomer} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={columnSpacing}
        >
          <TextField
            sx={defaultTextFieldWidth}
            id={"name"}
            variant="standard"
            label={t('form.fields.customer.customerName')}
            required
            {...register("name", {
              required: t('form.fields.customer.customerNameRequired')
            })}
            error={!!errors.name}
            helperText={errors.name?.message}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"doctorName"}
            variant="standard"
            label={t('form.fields.customer.doctorName')}
            required
            {...register("doctorName", {
              required: t('form.fields.customer.customerName')
            })}
            error={!!errors.doctorName}
            helperText={errors.doctorName?.message}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"customerNumber"}
            variant="standard"
            label={t('form.fields.customer.customerNumber')}
            required
            {...register("customerNumber", {
              required: t('form.fields.customer.customerNumberRequired')
            })}
            error={!!errors.customerNumber}
            helperText={errors.customerNumber?.message}
            disabled={!isAdmin}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={columnSpacing}
        >
          <TextField
            sx={defaultTextFieldWidth}
            id={"streetAndDwellingNumber"}
            variant="standard"
            label={t('form.fields.address.streetAndHouseNumber')}
            {...register("streetAndDwellingNumber")}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"zip"}
            variant="standard"
            label={t('form.fields.address.zipCode')}
            {...register("zip")}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"location"}
            variant="standard"
            label={t('form.fields.address.location')}
            required
            {...register("location", {
              required: t('form.fields.address.locationRequired')
            })}
            error={!!errors.location}
            helperText={errors.location?.message}
            disabled={!isAdmin}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={columnSpacing}
        >
          <TextField
            sx={defaultTextFieldWidth}
            id={"biotronikClientName"}
            variant="standard"
            label={t('form.fields.customer.biotronikClientName')}
            required
            {...register("biotronikClientName", {
              required: t('form.fields.customer.biotronikClientNameRequired')
            })}
            error={!!errors.biotronikClientName}
            helperText={errors.biotronikClientName?.message}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"biotronikCustomerNumber"}
            variant="standard"
            label={t('form.fields.customer.biotronikCustomerNumber')}
            required
            {...register("biotronikCustomerNumber", {
              required: t('form.fields.customer.biotronikCustomerNumberRequired')
            })}
            error={!!errors.biotronikCustomerNumber}
            helperText={errors.biotronikCustomerNumber?.message}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"eTin"}
            variant="standard"
            label={t('form.fields.customer.biotronikETin')}
            required
            {...register("eTin", {
              required: t('form.fields.customer.biotronikETinRequired')
            })}
            error={!!errors.eTin}
            helperText={errors.eTin?.message}
            disabled={!isAdmin}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={columnSpacing}
        >
          <TextField
            sx={defaultTextFieldWidth}
            id={"bostonSAPID"}
            variant="standard"
            label={t('form.fields.customer.bostonSAPID')}
            {...register("bostonSAPID")}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"abbottId"}
            variant="standard"
            label={t('form.fields.customer.abbottID')}
            {...register("abbottId")}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"microportId"}
            variant="standard"
            label={t('form.fields.customer.microportID')}
            {...register("microportId")}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"nonGBAGroupName"}
            variant="standard"
            label={t('form.fields.customer.nonGBAGroupName')}
            {...register("nonGbaGroupName")}
            disabled={!isAdmin}
          />
          <TextField
            sx={defaultTextFieldWidth}
            id={"gbaGroupName"}
            variant="standard"
            label={t('form.fields.customer.gbaGroupName')}
            {...register("gbaGroupName")}
            disabled={!isAdmin}
          />
          <FormControlLabel
            control={
              <Switch
                checked={watch("billingInHfGroups")}
                onChange={(e) => {
                  setValue("billingInHfGroups", e.target.checked, { shouldDirty: true })
                }}
                disabled={!isAdmin}
              />
            }
            label={t('form.fields.customer.billingInHfGroups')}
          />
        </Stack>
        <Collapse in={!!watch("billingInHfGroups")} unmountOnExit>
          <HfBillingGroupsEditor
            value={watch("hfBillingGroups") ?? []}
            onChange={(next) => setValue("hfBillingGroups", next, { shouldDirty: true })}
            disabled={!isAdmin}
          />
        </Collapse>
      </Stack>
    </form>
  )
}

interface HfBillingGroupsEditorProps {
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

function HfBillingGroupsEditor({value, onChange, disabled}: HfBillingGroupsEditorProps) {
  const {t} = useTranslation()
  const [draft, setDraft] = React.useState("")

  function commitDraft() {
    const trimmed = draft.trim()
    if (!trimmed) return
    if (value.includes(trimmed)) {
      setDraft("")
      return
    }
    onChange([...value, trimmed])
    setDraft("")
  }

  function removeAt(index: number) {
    onChange(value.filter((_, i) => i !== index))
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 2,
        marginTop: 1,
      }}
    >
      <Typography variant="body2" sx={{marginBottom: 1}}>
        {t('form.fields.customer.hfBillingGroupsHelp')}
      </Typography>
      <Stack direction="row" spacing={1} sx={{alignItems: 'center', marginBottom: 1}}>
        <TextField
          size="small"
          variant="outlined"
          label={t('form.fields.customer.hfBillingGroupsAddLabel')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitDraft()
            }
          }}
          disabled={disabled}
          sx={{minWidth: 240}}
        />
        <IconButton
          aria-label={t('form.fields.customer.hfBillingGroupsAddLabel')}
          onClick={commitDraft}
          disabled={disabled || !draft.trim()}
        >
          <AddIcon/>
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{flexWrap: 'wrap'}}>
        {value.map((group, index) => (
          <Chip
            key={`${group}-${index}`}
            label={`${index + 1}. ${group}`}
            onDelete={disabled ? undefined : () => removeAt(index)}
            sx={{marginBottom: 1}}
          />
        ))}
      </Stack>
    </Box>
  )
}
